import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { appointmentsAPI } from "@/lib/appointments-api";
import {
  usePatientFamilyDiseaseHistory,
  usePatientLifestyle,
  usePatientActivity,
  usePatientSurgeries,
  usePatientConcerns,
  usePatientInfo,
  useAppointmentHistory,
  useLifestyleTypes,
  useLifestyleFrequencies,
  useActivityTypes,
  useActivityFrequencies,
  useActivityDurations,
  useVitalsHome,
  usePatientSymptoms,
  useDeletePatientSymptom,
  useDeleteFamilyDiseaseHistory,
  useCreateFamilyDiseaseHistory,
  usePatientDiseaseHistory,
  useInitializeAppointmentDiseaseDraft,
} from "@/hooks/useConsultation";
import {
  AddDiseaseHistoryDialog,
  FatherDiseaseHistoryDialog,
  MotherDiseaseHistoryDialog,
  SiblingDiseaseHistoryDialog,
  AddLifestyleDialog,
  AddActivityDialog,
  AddSurgeryDialog,
  AddPatientConcernDialog,
  AddVitalsDialog,
  AddSymptomsDialog,
} from "@/components/consultation";
import { useToast } from "@/hooks/use-toast";
import {
  useBeforeUnloadGuard,
  useConfirmUnsaved,
} from "@/hooks/useUnsavedChanges";
import { UnsavedChangesDialog } from "@/components/UnsavedChangesDialog";

// Layout Components
import { ConsultationLayout } from "@/components/consultation/layout/ConsultationLayout";
import {
  ConsultationSidebar,
  ConsultationTab,
} from "@/components/consultation/layout/ConsultationSidebar";
import { BrowserWindow } from "@/components/consultation/layout/BrowserWindow";
import { PatientContextPanel } from "@/components/consultation/layout/PatientContextPanel";
import { PatientHeader } from "@/components/consultation/layout/PatientHeader";

// Tab Components
import { PatientOverview } from "@/components/consultation/tabs/PatientOverview";
import { SymptomsHome } from "@/components/consultation/tabs/SymptomsHome";
import { MedicinesHome } from "@/components/consultation/tabs/MedicinesHome";
import { ReportsHome } from "@/components/consultation/tabs/ReportsHome";
import { PrescriptionsHome } from "@/components/consultation/tabs/PrescriptionsHome";
import { PlaceholderTab } from "@/components/consultation/tabs/PlaceholderTab";
import { DocumentViewer } from "@/components/consultation/tabs/DocumentViewer";
import { NutritionEvaluationHome } from "@/components/consultation/tabs/nutrition";
import { TreatmentOverviewHome } from "@/components/consultation/tabs/treatment-overview";

interface ConsultationProps {
  appointmentId?: string;
}

interface Tab {
  id: string;
  title: string;
  closable?: boolean;
  fileUrl?: string;
}

const Consultation = ({ appointmentId }: ConsultationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated: _isAuthenticated,
    isNutritionist,
    isPhysiotherapist,
  } = useAuthStore();
  const params = useParams<{ appointmentId: string }>();
  const activeAppointmentId = appointmentId || params.appointmentId;

  // Get prescription URL from navigation state (passed from AppointmentDetailsDialog)
  const prescriptionUrlFromState = (
    location.state as { prescriptionUrl?: string | null }
  )?.prescriptionUrl;

  // Determine source based on logged-in user type
  const diseaseSource = isNutritionist() ? "meal_plan" : "prescription";

  // State for appointment details to get patient ID
  const [patientId, setPatientId] = useState<string>("");

  // Layout State — sidebar tab synced with URL so it survives refresh
  const VALID_CONSULTATION_TABS: Set<string> = new Set([
    "overview",
    "symptoms",
    "medicines",
    "reports",
    "family",
    "lifestyle",
    "prescription",
    "nutrition",
    "treatment-overview",
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: ConsultationTab = VALID_CONSULTATION_TABS.has(
    searchParams.get("tab") || ""
  )
    ? (searchParams.get("tab") as ConsultationTab)
    : "overview";
  const setActiveTab = useCallback(
    (tab: ConsultationTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [internalTabs, setInternalTabs] = useState<Tab[]>([
    { id: "home", title: "Profile & History Home", closable: false },
  ]);
  const [activeInternalTabId, setActiveInternalTabId] = useState("home");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // React Query hooks for data fetching
  const { data: appointmentDetails, isLoading: isAppointmentLoading } =
    useQuery({
      queryKey: ["appointmentDetails", activeAppointmentId],
      queryFn: () =>
        activeAppointmentId
          ? appointmentsAPI.getAppointmentDetails(activeAppointmentId)
          : null,
      enabled: !!activeAppointmentId,
    });

  const { data: patientInfo, isLoading: isPatientInfoLoading } = usePatientInfo(
    appointmentDetails?.patient_id || "",
    activeAppointmentId || ""
  );

  const { data: patientConcerns = [], isLoading: _isConcernsLoading } =
    usePatientConcerns(activeAppointmentId || "");

  // Appointment draft history feeds the AddDiseaseHistoryDialog so the dialog
  // sees the same in-flight chips that PreExistingConditionsWidget renders.
  const { data: appointmentDiseaseHistory = [] } = usePatientDiseaseHistory(
    activeAppointmentId || "",
    diseaseSource
  );

  const { data: familyHistory = [], isLoading: _isFamilyHistoryLoading } =
    usePatientFamilyDiseaseHistory(appointmentDetails?.patient_id || "");

  const { data: patientLifestyle = [], isLoading: _isLifestyleLoading } =
    usePatientLifestyle(activeAppointmentId || "");

  const { data: patientActivity = [], isLoading: _isActivityLoading } =
    usePatientActivity(activeAppointmentId || "");

  const { data: patientSurgeries = [], isLoading: _isSurgeriesLoading } =
    usePatientSurgeries(activeAppointmentId || "");

  const { data: lastVisits = [], isLoading: _isLastVisitsLoading } =
    useAppointmentHistory(appointmentDetails?.patient_id || "", "completed");

  // Fetch catalogs for frequency badges
  const { data: lifestyleTypes = [] } = useLifestyleTypes();
  const { data: lifestyleFrequencies = [] } = useLifestyleFrequencies();
  const { data: activityTypes = [] } = useActivityTypes();
  const { data: activityFrequencies = [] } = useActivityFrequencies();
  const { data: activityDurations = [] } = useActivityDurations();

  // Vitals & Symptoms Data
  const { data: vitalsData } = useVitalsHome(activeAppointmentId || "");
  const { data: patientSymptoms = [] } = usePatientSymptoms(
    activeAppointmentId || ""
  );
  const deleteSymptom = useDeletePatientSymptom();

  // The disease initialize endpoint also seeds AppointmentSymptomsDraft from
  // committed PatientSymptoms. Without this, a doctor landing directly on the
  // Symptoms tab would not see the patient's existing symptoms (no disease
  // widget mounts on that tab to trigger lazy-init). Hoisting the call here
  // makes it a one-time per (appointment, source); the per-card refs in
  // DiagnosisCard / PreExistingConditionsWidget are still in place as a guard
  // when those cards mount before this effect runs.
  const { mutate: initializeDraft } = useInitializeAppointmentDiseaseDraft();
  const initializedDraftRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeAppointmentId) return;
    const key = `${activeAppointmentId}:${diseaseSource}`;
    if (initializedDraftRef.current === key) return;
    initializedDraftRef.current = key;
    initializeDraft({
      appointmentId: activeAppointmentId,
      source: diseaseSource,
    });
  }, [activeAppointmentId, diseaseSource, initializeDraft]);

  const deleteFamilyDiseaseHistory = useDeleteFamilyDiseaseHistory();
  const createFamilyDiseaseHistory = useCreateFamilyDiseaseHistory();

  const _isLoading = isAppointmentLoading || isPatientInfoLoading;

  // Check if appointment is completed - hide add buttons and prevent dialogs
  const isCompleted = appointmentDetails?.appointment_status === "completed";

  // Dialog state
  const [diseaseHistoryDialogOpen, setDiseaseHistoryDialogOpen] =
    useState(false);
  const [fatherHistoryDialogOpen, setFatherHistoryDialogOpen] = useState(false);
  const [motherHistoryDialogOpen, setMotherHistoryDialogOpen] = useState(false);
  const [siblingHistoryDialogOpen, setSiblingHistoryDialogOpen] =
    useState(false);
  const [lifestyleDialogOpen, setLifestyleDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [surgeryDialogOpen, setSurgeryDialogOpen] = useState(false);
  const [concernDialogOpen, setConcernDialogOpen] = useState(false);
  const [vitalsDialogOpen, setVitalsDialogOpen] = useState(false);
  const [symptomsDialogOpen, setSymptomsDialogOpen] = useState(false);

  const { toast: _toast } = useToast();

  useBeforeUnloadGuard();
  const confirmUnsaved = useConfirmUnsaved();

  // Reset state when appointmentId changes (switching sessions), but NOT on
  // initial mount/refresh — the URL already has the correct tab in that case.
  const prevAppointmentId = useRef(activeAppointmentId);
  useEffect(() => {
    const isInitialMount = prevAppointmentId.current === activeAppointmentId;
    prevAppointmentId.current = activeAppointmentId;
    if (isInitialMount) return;

    // Reset patientId to prevent stale data from previous session
    setPatientId("");
    // Reset UI state
    setActiveTab("overview");
    setActiveInternalTabId("home");
    setInternalTabs([
      { id: "home", title: "Profile & History Home", closable: false },
    ]);
    // Close any open dialogs
    setDiseaseHistoryDialogOpen(false);
    setFatherHistoryDialogOpen(false);
    setMotherHistoryDialogOpen(false);
    setSiblingHistoryDialogOpen(false);
    setLifestyleDialogOpen(false);
    setActivityDialogOpen(false);
    setSurgeryDialogOpen(false);
    setConcernDialogOpen(false);
    setVitalsDialogOpen(false);
    setSymptomsDialogOpen(false);
  }, [activeAppointmentId]);

  // Update patientId when appointment details are loaded
  useEffect(() => {
    if (appointmentDetails?.patient_id) {
      setPatientId(appointmentDetails.patient_id);
    }
  }, [appointmentDetails?.patient_id]);

  const handleClose = () => {
    navigate(-1);
  };

  // Dialog handlers - prevent opening if appointment is completed
  const handleAddConfirmed = () => {
    if (!isCompleted) setDiseaseHistoryDialogOpen(true);
  };
  const handleManageFatherHistory = () =>
    !isCompleted && setFatherHistoryDialogOpen(true);
  const handleManageMotherHistory = () =>
    !isCompleted && setMotherHistoryDialogOpen(true);
  const handleManageSiblingHistory = () =>
    !isCompleted && setSiblingHistoryDialogOpen(true);
  const handleManageLifestyle = () =>
    !isCompleted && setLifestyleDialogOpen(true);
  const handleManageActivity = () =>
    !isCompleted && setActivityDialogOpen(true);
  const handleManageSurgery = () => !isCompleted && setSurgeryDialogOpen(true);
  const handleManageConcern = () => !isCompleted && setConcernDialogOpen(true);
  const handleAddVitals = () => !isCompleted && setVitalsDialogOpen(true);
  const handleAddSymptoms = () => !isCompleted && setSymptomsDialogOpen(true);
  const handleDeleteSymptom = (symptomId: string) => {
    if (activeAppointmentId && !isCompleted) {
      deleteSymptom.mutate({ symptomId, appointmentId: activeAppointmentId });
    }
  };
  const handleRemoveFamilyDiseaseHistory = (historyId: number | string) => {
    if (patientId && !isCompleted) {
      deleteFamilyDiseaseHistory.mutate({
        historyId: String(historyId),
        patientId,
      });
    }
  };
  const handleReAddFamilyDiseaseHistory = (
    payload: Record<string, unknown>
  ) => {
    if (patientId && !isCompleted) {
      createFamilyDiseaseHistory.mutate({
        patient: patientId,
        ...payload,
      } as Parameters<typeof createFamilyDiseaseHistory.mutate>[0]);
    }
  };

  // ... existing imports

  // Tab Management
  const handleSidebarTabChange = (tab: ConsultationTab) => {
    // Same-tab clicks shouldn't trigger the unsaved-changes dialog.
    if (tab === activeTab) return;
    confirmUnsaved(() => {
      setActiveTab(tab);
      // Update the first tab's title based on sidebar selection
      let homeTitle = "Home";
      switch (tab) {
        case "overview":
          homeTitle = "Profile & History Home";
          break;
        case "medicines":
          homeTitle = "Medications Home";
          break;
        case "symptoms":
          homeTitle = "Vitals & Symptoms Home";
          break;
        case "reports":
          homeTitle = "Investigations Home";
          break;
        case "family":
          homeTitle = "Family History";
          break;
        case "lifestyle":
          homeTitle = "Lifestyle Home";
          break;
        case "prescription":
          homeTitle = "Prescription";
          break;
        case "treatment-overview":
          homeTitle = "Treatment History";
          break;
      }

      setInternalTabs((prev) => {
        const newTabs = [...prev];
        if (newTabs.length > 0) {
          newTabs[0] = { ...newTabs[0], title: homeTitle };
        }
        return newTabs;
      });

      // Switch to the first tab when sidebar changes
      setActiveInternalTabId("home");
    });
  };

  const handleOpenDocumentTab = (doc: any) => {
    const docTabId = `doc-${doc.id}`;

    // Extract a meaningful title: use description, or extract filename from URL
    let title = doc.description?.trim() || "";
    if (!title && doc.file) {
      try {
        const urlObj = new URL(doc.file);
        const rawName = urlObj.pathname.split("/").pop() || "";
        title = decodeURIComponent(rawName);
      } catch {
        const raw = doc.file.split("?")[0].split("/").pop() || "";
        title = decodeURIComponent(raw);
      }
    }
    if (!title) title = "Document";

    // Check if tab already exists
    if (!internalTabs.find((t) => t.id === docTabId)) {
      setInternalTabs((prev) => [
        ...prev,
        {
          id: docTabId,
          title,
          closable: true,
          fileUrl: doc.file,
        },
      ]);
    }

    setActiveInternalTabId(docTabId);
  };

  const handleInternalTabClose = (id: string) => {
    const newTabs = internalTabs.filter((t) => t.id !== id);
    setInternalTabs(newTabs);
    if (activeInternalTabId === id) {
      // If closing active tab, switch to the last available tab
      setActiveInternalTabId(newTabs[newTabs.length - 1]?.id || "home");
    }
  };

  // ... existing code

  // Render content based on active sidebar tab and active internal tab
  const renderContent = () => {
    // If we are on the "home" tab, show the main view for the section
    if (activeInternalTabId === "home") {
      switch (activeTab) {
        case "overview":
          return (
            <PatientOverview
              patientInfo={patientInfo}
              appointmentStartedAt={appointmentDetails?.start_time}
              patientId={patientId}
              appointmentId={activeAppointmentId}
              familyHistory={familyHistory}
              patientLifestyle={patientLifestyle}
              patientActivity={patientActivity}
              lifestyleTypes={lifestyleTypes}
              lifestyleFrequencies={lifestyleFrequencies}
              activityTypes={activityTypes}
              activityFrequencies={activityFrequencies}
              activityDurations={activityDurations}
              lastVisits={lastVisits}
              onAddConfirmed={handleAddConfirmed}
              onManageFatherHistory={handleManageFatherHistory}
              onManageMotherHistory={handleManageMotherHistory}
              onManageSiblingHistory={handleManageSiblingHistory}
              onRemoveFamilyDiseaseHistory={handleRemoveFamilyDiseaseHistory}
              onReAddFamilyDiseaseHistory={handleReAddFamilyDiseaseHistory}
              onManageLifestyle={handleManageLifestyle}
              onManageActivity={handleManageActivity}
              onOpenDocument={handleOpenDocumentTab}
              isCompleted={isCompleted}
            />
          );
        case "medicines":
          return <MedicinesHome isCompleted={isCompleted} />;
        case "symptoms":
          return (
            <SymptomsHome
              vitalsData={vitalsData}
              patientSymptoms={patientSymptoms}
              patientInfo={patientInfo}
              onAddVitals={handleAddVitals}
              onAddSymptoms={handleAddSymptoms}
              onDeleteSymptom={handleDeleteSymptom}
              isCompleted={isCompleted}
            />
          );
        case "reports":
          return (
            <ReportsHome
              onOpenDocument={handleOpenDocumentTab}
              isCompleted={isCompleted}
            />
          );
        case "prescription":
          return (
            <PrescriptionsHome
              familyHistory={familyHistory}
              patientConcerns={patientConcerns}
              activeAppointmentId={activeAppointmentId}
              patientId={patientId}
              onManageFatherHistory={handleManageFatherHistory}
              onManageMotherHistory={handleManageMotherHistory}
              onManageSiblingHistory={handleManageSiblingHistory}
              onManageConcern={handleManageConcern}
              isCompleted={isCompleted}
              prescriptionUrl={
                prescriptionUrlFromState || appointmentDetails?.prescription_url
              }
            />
          );
        case "nutrition":
          return (
            <NutritionEvaluationHome
              appointmentId={activeAppointmentId || ""}
              patientId={patientId}
              isCompleted={isCompleted}
              prescriptionUrl={
                prescriptionUrlFromState || appointmentDetails?.prescription_url
              }
            />
          );
        case "treatment-overview":
          return (
            <TreatmentOverviewHome
              appointmentId={activeAppointmentId}
              onOpenDocumentTab={handleOpenDocumentTab}
              showHeader={false}
              embedded
            />
          );
        default:
          return <PlaceholderTab title={activeTab} />;
      }
    }

    // Handle document tabs
    if (activeInternalTabId.startsWith("doc-")) {
      const docId = activeInternalTabId.replace("doc-", "");
      const tab = internalTabs.find((t) => t.id === activeInternalTabId);
      return (
        <DocumentViewer
          documentId={docId}
          title={tab?.title || "Document"}
          fileUrl={tab?.fileUrl}
        />
      );
    }

    // Handle other internal tabs (e.g. documents) here
    return <PlaceholderTab title={`Document: ${activeInternalTabId}`} />;
  };

  return (
    <>
      <ConsultationLayout
        sidebar={
          <ConsultationSidebar
            patientInfo={patientInfo}
            activeTab={activeTab}
            onTabChange={handleSidebarTabChange}
            onClose={handleClose}
            isCollapsed={isSidebarCollapsed}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            userRole={
              isNutritionist()
                ? "nutritionist"
                : isPhysiotherapist()
                  ? "physiotherapist"
                  : "doctor"
            }
          />
        }
        isSidebarCollapsed={isSidebarCollapsed}
        rightPanel={
          <PatientContextPanel
            patientInfo={patientInfo}
            patientConcerns={patientConcerns}
            patientSurgeries={patientSurgeries}
            activeAppointmentId={activeAppointmentId}
            onManageConcern={handleManageConcern}
            onManageSurgery={handleManageSurgery}
            hideHistoryAndConcerns={activeTab === "prescription"}
            showHealthData={activeTab === "nutrition"}
            isCompleted={isCompleted}
            zarityAppointmentId={appointmentDetails?.zarity_appointment_id}
            appointmentStartTime={appointmentDetails?.start_time}
            appointmentDuration={appointmentDetails?.service_event_duration}
          />
        }
      >
        {/* TODO: remove once compact rail layout is confirmed */}
        {/* eslint-disable-next-line no-constant-binary-expression */}
        {false && <PatientHeader patientInfo={patientInfo} />}
        <BrowserWindow
          tabs={internalTabs}
          activeTabId={activeInternalTabId}
          onTabClick={setActiveInternalTabId}
          onTabClose={handleInternalTabClose}
          className={
            activeTab === "treatment-overview" && activeInternalTabId === "home"
              ? "h-auto max-h-full min-h-0 shrink"
              : "flex-1 min-h-0 h-auto"
          }
          hideTabs={
            activeTab === "prescription" ||
            activeTab === "nutrition" ||
            activeTab === "symptoms"
          }
        >
          {renderContent()}
        </BrowserWindow>
      </ConsultationLayout>

      {/* Dialogs */}
      <AddDiseaseHistoryDialog
        open={diseaseHistoryDialogOpen}
        onOpenChange={setDiseaseHistoryDialogOpen}
        patientId={patientId}
        appointmentId={activeAppointmentId!}
        confirmedHistory={appointmentDiseaseHistory.filter(
          (h) =>
            h.activity_status !== "inactive" &&
            (h.draft_context === "past" || h.draft_context === "in_progress") &&
            (h.disease_status === "confirmed" ||
              h.disease_status === "provisional")
        )}
        selfReportedHistory={appointmentDiseaseHistory.filter(
          (h) =>
            h.activity_status !== "inactive" &&
            h.disease_status === "self_reported"
        )}
        defaultSource={diseaseSource}
      />

      <FatherDiseaseHistoryDialog
        open={fatherHistoryDialogOpen}
        onOpenChange={setFatherHistoryDialogOpen}
        patientId={patientId}
        patientFamilyHistory={familyHistory}
      />

      <MotherDiseaseHistoryDialog
        open={motherHistoryDialogOpen}
        onOpenChange={setMotherHistoryDialogOpen}
        patientId={patientId}
        patientFamilyHistory={familyHistory}
      />

      <SiblingDiseaseHistoryDialog
        open={siblingHistoryDialogOpen}
        onOpenChange={setSiblingHistoryDialogOpen}
        patientId={patientId}
        patientFamilyHistory={familyHistory}
      />

      <AddLifestyleDialog
        open={lifestyleDialogOpen}
        onOpenChange={setLifestyleDialogOpen}
        patientId={patientId}
        appointmentId={activeAppointmentId!}
        patientLifestyle={patientLifestyle}
      />

      <AddActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        appointmentId={activeAppointmentId!}
        patientActivities={patientActivity}
      />

      <AddSurgeryDialog
        open={surgeryDialogOpen}
        onOpenChange={setSurgeryDialogOpen}
        appointmentId={activeAppointmentId!}
        patientSurgeries={patientSurgeries}
      />

      <AddPatientConcernDialog
        open={concernDialogOpen}
        onOpenChange={setConcernDialogOpen}
        appointmentId={activeAppointmentId!}
        patientConcerns={patientConcerns.filter(
          (c) => c.appointment === activeAppointmentId || !activeAppointmentId
        )}
      />

      <AddVitalsDialog
        open={vitalsDialogOpen}
        onOpenChange={setVitalsDialogOpen}
        appointmentId={activeAppointmentId!}
        patientId={patientId}
      />

      <AddSymptomsDialog
        open={symptomsDialogOpen}
        onOpenChange={setSymptomsDialogOpen}
        appointmentId={activeAppointmentId!}
      />

      <UnsavedChangesDialog />
    </>
  );
};

export default Consultation;
